/**
 * Browser identification POC: pure helpers (no React, no DOM side effects).
 *
 * Contract (shared with the Android app; do not change without both sides):
 *   Input:    /pixel/tracking/identify?uid={uid}&requestId={requestId}
 *   Pixel:    GET https://track.activemetering.com/pixel/v1/identify/1.gif?uid={uid}
 *   Callback: surveyjunkie://com.surveyjunkie/pixel-tracking/callback?status={success|failed}&requestId={requestId}
 *             (requestId is omitted when the request had none; status is then always `failed`)
 *
 * SECURITY (production blocker): the `uid` binding is NOT authenticated. Anyone who can load this
 * page can associate an arbitrary user ID with the browser. Production must authenticate or otherwise
 * protect this binding before the page is exposed.
 *
 * COOKIE LIMITATION: "success" only means the pixel image loaded (onload). It does NOT prove that a
 * cookie was stored, nor that it will be sent on third-party requests. JS cannot read cookies on
 * track.activemetering.com, so cookie storage is always reported as "unverified".
 */

export const PIXEL_TRACKING_IDENTIFY_PATH = '/pixel/tracking/identify';
export const IDENTIFY_PIXEL_BASE_URL = 'https://track.activemetering.com/pixel/v1/identify/1.gif';
export const APP_CALLBACK_BASE_URL = 'surveyjunkie://com.surveyjunkie/pixel-tracking/callback';
/** Time to wait for the pixel before reporting `failed`. */
export const IDENTIFY_PIXEL_TIMEOUT_MS = 10000;

export type IdentifyStatus = 'success' | 'failed';

export interface IdentifyParams {
  uid: string | null;
  requestId: string | null;
}

/** Reads `uid` and `requestId`; blank or whitespace-only values are treated as missing. */
export const readIdentifyParams = (search: string): IdentifyParams => {
  const params = new URLSearchParams(search);
  const read = (name: string): string | null => (params.get(name) ?? '').trim() || null;

  return { uid: read('uid'), requestId: read('requestId') };
};

export const buildPixelUrl = (uid: string): string =>
  `${IDENTIFY_PIXEL_BASE_URL}?uid=${encodeURIComponent(uid)}`;

/**
 * Uses encodeURIComponent (not URLSearchParams) so spaces become %20. Android's Uri.getQueryParameter
 * does not decode '+' as a space.
 */
export const buildCallbackUrl = (status: IdentifyStatus, requestId: string | null): string =>
  requestId
    ? `${APP_CALLBACK_BASE_URL}?status=${status}&requestId=${encodeURIComponent(requestId)}`
    : `${APP_CALLBACK_BASE_URL}?status=${status}`;
